// Local landing pages. Council links and recycling-centre names were checked
// against the councils' own pages on 24 October 2026. Carmarthen, Ammanford,
// Gorseinon and Pontardawe were checked again on 29 October 2026 (see
// newAreas.js). Do not add street addresses, carrier counts, or registration
// numbers to this file.
//
// Fly-tipping:
//   Swansea        https://www.swansea.gov.uk/reportflytipping
//   Neath / PT     https://www.npt.gov.uk/parking-roads-and-travel/problems-with-roads-and-streets/fly-tipping/
//   Maesteg / Bridgend  https://www.bridgend.gov.uk/council/customer-services/report-an-issue/report-fly-tipping/
//   Llanelli       https://www.carmarthenshire.gov.wales/council-services/recycling-bins-and-litter/litter/fly-tipping/
//                  https://myaccount.carmarthenshire.gov.wales/en/service/Report_fly_tipping_process
//   Cardiff        https://www.cardiff.gov.uk/fly-tipping
//                  (Natural Resources Wales links here. The page explains the offence.
//                  The council's app page lists fly-tipping under Report a problem.)
// Recycling centres:
//   Swansea: Llansamlet, Clyne, Penlan, Garngoch, Tir John
//            https://www.swansea.gov.uk/recyclingcentres
//   NPT: Briton Ferry, Cymmer, and Lower Cwmtwrch (Powys). Slot booking required.
//        Re-checked 29 October 2026. Lower Cwmtwrch was not in the 24 October note.
//        https://www.npt.gov.uk/bins-and-recycling/recycling-and-sustainability/recycling-centres/
//   Bridgend county: Brynmenyn, Maesteg, Pyle
//        https://www.bridgend.gov.uk/residents/recycling-and-waste/recycling-centres/
//   Llanelli: Trostre (also Nantycaws, Wernddu, Whitland elsewhere in the county)
//        https://www.carmarthenshire.gov.wales/council-services/recycling-bins-and-litter/recycling-centres/trostre-llanelli/
//   Cardiff: Lamby Way, Bessemer Close
//        https://www.cardiff.gov.uk/ENG/resident/Rubbish-and-recycling/recycling-centres/Pages/default.aspx

import { EA_CARRIER_REGISTER, NRW_CARRIER_REGISTER } from './site';
import { NEW_AREAS } from './newAreas';

export const NRW_REGISTER = NRW_CARRIER_REGISTER;
export const EA_REGISTER = EA_CARRIER_REGISTER;

const link = (text, href) => ({ t: text, href });

// Plain check line. No registration numbers in this copy.
const registerCheck = (after = '') => [
  'Registered in England? Check the ',
  link('Environment Agency register', EA_REGISTER),
  '. Registered in Wales? Check the ',
  link('Natural Resources Wales register', NRW_REGISTER),
  after ? `. ${after}` : '.',
];

export const AREAS = [
  {
    slug: 'swansea',
    name: 'Swansea',
    path: '/areas/swansea',
    adminArea: 'City and County of Swansea',
    title: 'Rubbish removal in Swansea | The WasteGame',
    description: 'Post a Swansea rubbish removal job for free. Registered waste carriers send prices. Pay by card only after the job is marked complete. No customer fee.',
    h1: 'Rubbish removal in Swansea',
    eyebrow: 'Swansea, South Wales',
    card: 'End-of-tenancy flats in Brynmill, garden waste from Killay, and sofas that will not come down a terrace stair.',
    intro: [
      'Swansea is not one kind of rubbish job. A shared house in Brynmill at the end of June, a garden in Killay after a hedge cut, and a sofa stuck on the stairs of a flat in SA1 are different collections. This page is for people in Swansea who want to ask registered waste carriers for a price, without paying to ask.',
      'The WasteGame is a new marketplace, based in South Wales. Posting a job is free. It does not book a van, and it does not mean anyone will offer a price. If carriers want the work, they send their own prices. You pick one, or you pick none.',
    ],
    howLead: 'The steps are the same in every town. In Swansea, the detail that changes the price is access: a rear lane in Hafod or St Thomas, a hill in Townhill, a lift in the Maritime Quarter, or a driveway in Gowerton or Pontarddulais.',
    jobs: [
      {
        icon: '🌿',
        title: 'Garden waste',
        text: 'Gardens in Sketty, Derwen Fawr, Mumbles and out towards Gower throw up cuttings, soil and old timber that will not fit the kerbside green collection. Say whether it is bagged, and whether a van can reach the gate.',
      },
      {
        icon: '🛋️',
        title: 'Furniture',
        text: 'Sofas, wardrobes and beds from terraces in Landore, Port Tennant and the city centre, and from flats where the lift is small. If it has to come down stairs, say which floor and whether it will bend around the landing.',
      },
      {
        icon: '🛏️',
        title: 'Mattresses',
        text: 'Common when a tenancy ends in Uplands, Brynmill and around the university. Say if it is a single or a double, how many there are, and if it is wet. Swansea’s recycling centres do not treat every mattress type the same, so a collection is sometimes the practical option.',
      },
      {
        icon: '🧱',
        title: 'Rubble',
        text: 'A small amount from a house: a path, a fireplace, a bathroom ripped out at home. Say how many bags, and that it is household DIY. This is not a booking for a commercial building site.',
      },
      {
        icon: '🏠',
        title: 'House and end-of-tenancy clearance',
        text: 'A room, a student flat, or a family house in Sketty or Cockett when someone moves out. List the large items and say what, if anything, stays for the next person. A clearance of one bedroom is a different job from emptying a whole house.',
      },
    ],
    localTitle: 'Swansea Council tips and fly-tipping',
    local: [
      [
        'If you can take the waste yourself, Swansea Council runs household waste recycling centres at Llansamlet, Clyne, Penlan, Garngoch and Tir John. Llansamlet asks residents to book a slot, and the sites can ask for proof you live in Swansea. Check ',
        link('the council’s recycling centres page', 'https://www.swansea.gov.uk/recyclingcentres'),
        ' for what each site takes before you load the car.',
      ],
      [
        'If you see waste dumped in a lane, a lay-by or on open ground, report it on ',
        link('Swansea Council’s fly-tipping form', 'https://www.swansea.gov.uk/reportflytipping'),
        '. Do not move it. Fly-tipping is a criminal offence. If you pay someone to take waste, you can look them up. ',
        ...registerCheck('The WasteGame asks carriers to be registered waste carriers. That is not a promise that a particular carrier will take your job.'),
      ],
    ],
    faqs: [
      {
        q: 'Is it free to post a rubbish job in Swansea?',
        a: 'Yes. Creating the job costs nothing. You only pay a carrier if you accept their price, and on a priced job you pay that price by card after the job is marked complete. The WasteGame does not add a customer fee.',
      },
      {
        q: 'Does posting a job book a collection in Swansea?',
        a: 'No. The WasteGame is new. Registered waste carriers choose whether to send a price. If nobody does, you have not been charged. You can still use a Swansea Council recycling centre, or another legal way to get rid of the waste.',
      },
      {
        q: 'Where do I put my address?',
        a: 'On the job form, after you have a free customer account. The form asks for the pickup postcode and address so the carrier you choose can find the waste. The street is not published on the public job listing. Other people see a coarse area, such as the postcode district.',
      },
      {
        q: 'What should I write for a Swansea collection?',
        a: 'What the waste is, how much of it there is, which part of Swansea it is in, and anything awkward: stairs, a rear lane, no parking, a wet mattress, or bags of rubble. A photo helps carriers price it. You can add up to four.',
      },
      {
        q: 'Can I take a sofa to the tip instead?',
        a: [
          'Often, yes, if you have a car and the site accepts it. Start with ',
          link('Swansea’s recycling centres page', 'https://www.swansea.gov.uk/recyclingcentres'),
          '. A collection is for when you cannot move it yourself, or the site will not take it.',
        ],
      },
    ],
    nearby: ['neath', 'llanelli', 'gorseinon', 'pontardawe'],
  },
  {
    slug: 'neath',
    name: 'Neath',
    path: '/areas/neath',
    adminArea: 'Neath Port Talbot',
    title: 'Waste removal in Neath | The WasteGame',
    description: 'Post a Neath waste removal job for free. Registered waste carriers send prices. Pay by card only after the job is marked complete. No customer fee.',
    h1: 'Waste removal in Neath',
    eyebrow: 'Neath, Neath Port Talbot',
    card: 'Valley terraces, garden waste towards Cimla and Bryncoch, and furniture that will not fit a car to Briton Ferry.',
    intro: [
      'Neath town, Cimla, Bryncoch, Cadoxton and the houses up the Dulais and Neath valleys do not produce the same waste. A terrace with a narrow rear lane needs a different collection from a garden clearance on the edge of town. This page is for waste removal in Neath when you want prices from registered waste carriers, and you do not want to pay to ask.',
      'The WasteGame is new. A job on the site is a request for prices. It is not a booking, and nobody is obliged to come out. If a price arrives and you accept it, you pay that price by card only after the job is marked complete. There is no customer fee on top.',
    ],
    howLead: 'Say which part of Neath the waste is in. “Near the town centre” is less useful than Cimla, Tonna, Aberdulais, Skewen or a house up the valley, and less useful than saying the van has to stop on a hill.',
    jobs: [
      {
        icon: '🌿',
        title: 'Garden waste',
        text: 'Plots towards Cimla, Bryncoch and Cadoxton can leave a pile of branches, soil and old fence panels after a weekend. Bag what you can, and say if the pile is at the top of a garden the van cannot drive to.',
      },
      {
        icon: '🛋️',
        title: 'Furniture',
        text: 'Older housing in Neath town and the valleys still has sofas and wardrobes that will not fit in a family car, and will not wait for a council bulky collection. Name the items and the floor they are on.',
      },
      {
        icon: '🛏️',
        title: 'Mattresses',
        text: 'One mattress from a box room is a small job. Three mattresses from a house clearance are not. Say how many, the sizes, and whether they are damp. Damp waste is heavier and some sites will not take it.',
      },
      {
        icon: '🧱',
        title: 'Rubble',
        text: 'Stone, brick and soil from a garden wall or a small home job, bagged and from a household. Neath Port Talbot’s recycling centres charge for larger loads of building-type waste. Say how many bags so a carrier can judge the load. This page is not for commercial demolition.',
      },
      {
        icon: '🏠',
        title: 'House and end-of-tenancy clearance',
        text: 'A rented house being handed back, or a family home being emptied before a sale. List what is actually there. “Full house” without a list is hard to price, and a carrier may simply not offer.',
      },
    ],
    localTitle: 'Neath Port Talbot tips and fly-tipping',
    local: [
      [
        'Neath is in Neath Port Talbot. The council’s recycling centres page lists Briton Ferry, Cymmer and Lower Cwmtwrch (Powys), and you must book a slot before you visit. Briton Ferry is the site on the Neath side of the borough. Cymmer is up the Afan valley, a longer trip from most of Neath. Lower Cwmtwrch is in Powys. Details are on ',
        link('the council’s recycling centres page', 'https://www.npt.gov.uk/bins-and-recycling/recycling-and-sustainability/recycling-centres/'),
        '.',
      ],
      [
        'Dumped sofas and bags in rear lanes, or waste left on a country road, can be reported on ',
        link('Neath Port Talbot Council’s fly-tipping page', 'https://www.npt.gov.uk/parking-roads-and-travel/problems-with-roads-and-streets/fly-tipping/'),
        '. That page has separate online forms, including bulky waste in a residential area and waste in the countryside. Do not touch the waste. ',
        ...registerCheck(),
      ],
    ],
    faqs: [
      {
        q: 'Is waste removal in Neath free to post?',
        a: 'Posting is free. You pay only if you accept a price, and only by card once that job is marked complete. The price you accept is what you pay. The WasteGame does not add a fee for customers.',
      },
      {
        q: 'If I post a Neath job today, is a collection booked?',
        a: 'No. Carriers on the site are registered waste carriers, and they decide which jobs to price. The WasteGame is new, so a Neath job may get a price, or it may sit with none. You are not charged for posting.',
      },
      {
        q: 'Which tip is for Neath residents?',
        a: [
          'Neath Port Talbot Council lists Briton Ferry, Cymmer and Lower Cwmtwrch (Powys), and you need a booked slot. Briton Ferry is the one most Neath households use. Read the current rules on ',
          link('the council’s recycling centres page', 'https://www.npt.gov.uk/bins-and-recycling/recycling-and-sustainability/recycling-centres/'),
          ' before you go. A collection is for waste you cannot take there yourself.',
        ],
      },
      {
        q: 'Do you collect from the valleys above Neath?',
        a: 'You can post a job for an address in the Neath area, including villages up the valley. Whether a carrier sends a price depends on the job and the journey. Post the location clearly. Do not assume someone covers every lane.',
      },
      {
        q: 'What is a registered waste carrier?',
        a: [
          'Someone who is on the public register to carry waste. ',
          ...registerCheck('The WasteGame does not show registration numbers on this page. A registration is not a promise that someone will take your job.'),
        ],
      },
    ],
    nearby: ['swansea', 'port-talbot'],
  },
  {
    slug: 'port-talbot',
    name: 'Port Talbot',
    path: '/areas/port-talbot',
    adminArea: 'Neath Port Talbot',
    title: 'House clearance in Port Talbot | The WasteGame',
    description: 'Post a Port Talbot house clearance or rubbish job for free. Registered waste carriers send prices. Pay by card only after the job is marked complete.',
    h1: 'House clearance in Port Talbot',
    eyebrow: 'Port Talbot, Neath Port Talbot',
    card: 'Household clearances in Aberavon, Sandfields and Baglan. Not steelworks waste, and not a skip for a commercial site.',
    intro: [
      'House clearance in Port Talbot usually means a home: a flat in Sandfields, a terrace in Aberavon or Taibach, a house in Baglan or Margam, or a property up the Afan valley. It does not mean waste from the steelworks or from a commercial yard. The WasteGame is for household and small domestic jobs posted by the person who needs them gone.',
      'Post the job for free. Registered waste carriers may send a price. You do not have to accept one. The site is new, so there may be no price at all. If you do accept a priced job, you pay that price by card only after it is marked complete, with no customer fee added.',
    ],
    howLead: 'Port Talbot’s roads and parking change the job. A flat off Aberavon seafront, a street in Sandfields with little space to stop, and a house up towards Cwmavon are not the same collection. Say which, and say if the waste is bagged or still in the rooms.',
    jobs: [
      {
        icon: '🏠',
        title: 'House and end-of-tenancy clearance',
        text: 'The main job people in Port Talbot ask about. A tenant moving out of a flat, or a family emptying a house. Walk the rooms and list the furniture, mattresses and bags. “Everything must go” is only useful if you also say how many rooms and what the big items are.',
      },
      {
        icon: '🛋️',
        title: 'Furniture',
        text: 'A sofa or a wardrobe from a Sandfields flat is often too big for the car and too awkward for the stairs. Say the floor, whether there is a lift, and if the item has to be dismantled.',
      },
      {
        icon: '🛏️',
        title: 'Mattresses',
        text: 'Name the size and how many. A stained or wet mattress should be described that way. Carriers price what they can see. A surprise at the door is how jobs get refused.',
      },
      {
        icon: '🌿',
        title: 'Garden waste',
        text: 'Baglan, Margam and the edges of town have gardens that produce soil, shrubs and old timber. A small bagged pile is a different price from a garden that has been let go for years. Say which you have.',
      },
      {
        icon: '🧱',
        title: 'Rubble',
        text: 'Household DIY only: a few bags of brick, tile or soil from your own house. Do not post industrial waste, factory scrap or a commercial demolition. If it came from a home and it is bagged, describe the bags.',
      },
    ],
    localTitle: 'Port Talbot tips and fly-tipping',
    local: [
      [
        'Port Talbot shares Neath Port Talbot Council with Neath. The council lists Briton Ferry, Cymmer and Lower Cwmtwrch (Powys), and residents book a slot before they visit. Briton Ferry sits on the Neath side of Port Talbot. Cymmer is the site for communities up the Afan valley. Lower Cwmtwrch is in Powys. These sites are for household waste, not for business waste. See ',
        link('recycling centres', 'https://www.npt.gov.uk/bins-and-recycling/recycling-and-sustainability/recycling-centres/'),
        '.',
      ],
      [
        'Fly-tipping on streets, back lanes and country roads can be reported through ',
        link('Neath Port Talbot Council’s fly-tipping page', 'https://www.npt.gov.uk/parking-roads-and-travel/problems-with-roads-and-streets/fly-tipping/'),
        '. Use the form that matches what you saw, such as bulky waste in a residential area or waste in the countryside. Leave the waste where it is. ',
        ...registerCheck(),
      ],
    ],
    faqs: [
      {
        q: 'Can I post a house clearance in Port Talbot for free?',
        a: 'Yes. The post is free. Payment, if you accept a price, happens by card after the job is marked complete. You pay the agreed price. There is no extra customer fee.',
      },
      {
        q: 'Do you clear commercial or steelworks waste?',
        a: 'No. These pages are for household rubbish, furniture, garden waste, small amounts of DIY rubble, and house or end-of-tenancy clearance. Industrial and trade waste needs a different arrangement.',
      },
      {
        q: 'What if no carrier sends a price?',
        a: 'Then nothing is booked and you pay nothing. Try a clearer description or photos, or take what you can to a Neath Port Talbot recycling centre if you live in the borough and the site accepts it. Book a slot first.',
      },
      {
        q: 'How do I report dumped waste in Port Talbot?',
        a: [
          'Use ',
          link('Neath Port Talbot Council’s fly-tipping page', 'https://www.npt.gov.uk/parking-roads-and-travel/problems-with-roads-and-streets/fly-tipping/'),
          '. The council asks you not to touch the waste or approach anyone you see dumping it.',
        ],
      },
      {
        q: 'When do I pay the carrier?',
        a: 'After the job is marked complete, and only if you accepted a price. The card payment is for that price. Posting, reading prices, and deciding not to accept one are all free.',
      },
    ],
    nearby: ['neath', 'maesteg', 'bridgend'],
  },
  {
    slug: 'maesteg',
    name: 'Maesteg',
    path: '/areas/maesteg',
    adminArea: 'Bridgend County Borough',
    title: 'Rubbish removal in Maesteg | The WasteGame',
    description: 'Post a Maesteg rubbish removal job for free. Registered waste carriers send prices for valley houses and gardens. Pay by card only when the job is complete.',
    h1: 'Rubbish removal in Maesteg',
    eyebrow: 'Maesteg, Llynfi valley',
    card: 'Hillside terraces in the Llynfi valley, the Maesteg recycling centre, and furniture that will not fit in a car down from Caerau.',
    intro: [
      'Maesteg sits in the Llynfi valley, in Bridgend County Borough, not in Neath Port Talbot. Terraces in Maesteg, Caerau, Nantyffyllon and Garth are often on slopes, with steps and little room to park. A rubbish job here is frequently “it will not fit in the car, and I cannot get it down to the recycling centre”.',
      'You can post that job on The WasteGame for free. The site is new. Registered waste carriers may send a price, or they may not. Accepting a price does not take a payment. You pay by card, the price you accepted, only once the job is marked complete. No customer fee is added.',
    ],
    howLead: 'For Maesteg, describe the hill and the parking before you describe the sofa. A carrier who cannot stop outside may not price the job. Name the village if it is not the town itself.',
    jobs: [
      {
        icon: '🛋️',
        title: 'Furniture',
        text: 'Sofas and wardrobes from valley terraces are the usual awkward load: heavy, up stairs, and then a tight street. Say the floor and whether the item comes apart. A photo of the stairwell is more useful than a long description.',
      },
      {
        icon: '🌿',
        title: 'Garden waste',
        text: 'Hillside gardens throw up soil, brambles and old timber, often at the back of the house where a van cannot go. Say if it is already at the front gate or still at the top of the garden.',
      },
      {
        icon: '🛏️',
        title: 'Mattresses',
        text: 'A mattress from a Caerau or Nantyffyllon bedroom still has to come down the stairs and into a van. Say the size. If you have more than one, say so in the same job rather than hoping a carrier guesses.',
      },
      {
        icon: '🧱',
        title: 'Rubble',
        text: 'A few bags from a home repair: a wall, a path, a fireplace. Keep it household. A carrier needs a bag count. Large building jobs are outside what this page is for.',
      },
      {
        icon: '🏠',
        title: 'House and end-of-tenancy clearance',
        text: 'Older valley houses can hold a lot of furniture in a small number of rooms. List the rooms you want cleared and the items that are not rubbish, if any are staying. A part-clearance and a full clearance should not be written up the same way.',
      },
    ],
    localTitle: 'Maesteg recycling centre and fly-tipping',
    local: [
      [
        'Bridgend County Borough Council runs community recycling centres at Maesteg, Pyle and Brynmenyn. The Maesteg centre is the local one. The council also has a reuse shop, The Sidings, at the Maesteg centre for household items in good condition. Each centre is closed one day a week, so check ',
        link('the council’s recycling centres page', 'https://www.bridgend.gov.uk/residents/recycling-and-waste/recycling-centres/'),
        ' before you drive down. The Sidings days are on the ',
        link('reuse shops page', 'https://www.bridgend.gov.uk/residents/recycling-and-waste/recycling-centres/reuse-shops-partnership/'),
        '.',
      ],
      [
        'Fly-tipping in the county is handled by Bridgend County Borough Council, including waste dumped on lanes above Maesteg. Report it on the ',
        link('council’s fly-tipping page', 'https://www.bridgend.gov.uk/council/customer-services/report-an-issue/report-fly-tipping/'),
        '. For waste on council-owned or public land, that page gives the email cleanupthecounty@bridgend.gov.uk. Private land is a different route, also explained there. Do not approach anyone you see dumping waste. ',
        ...registerCheck(),
      ],
    ],
    faqs: [
      {
        q: 'Is Maesteg covered by Neath Port Talbot Council?',
        a: 'No. Maesteg is in Bridgend County Borough. Rubbish you take yourself goes to Bridgend’s community recycling centres. The Maesteg centre is the local site. Fly-tipping reports go to Bridgend County Borough Council, not to Neath Port Talbot.',
      },
      {
        q: 'Is there a fee to post a Maesteg job?',
        a: 'No. Posting is free. Card payment happens only after you have accepted a price and the job is marked complete. You pay that price, and The WasteGame does not add a customer fee.',
      },
      {
        q: 'Can I donate furniture instead of binning it?',
        a: [
          'If it is in good condition, Bridgend Council’s reuse shop at the Maesteg centre is called The Sidings. Check days and what they take on the ',
          link('reuse shops page', 'https://www.bridgend.gov.uk/residents/recycling-and-waste/recycling-centres/reuse-shops-partnership/'),
          '. Broken furniture is a collection or a trip to the recycling centre, not a donation.',
        ],
      },
      {
        q: 'Will someone drive up to Caerau or Nantyffyllon?',
        a: 'Only if a registered waste carrier chooses to price that job. The valley roads and parking are part of the description, so include them. The WasteGame cannot promise a collection.',
      },
      {
        q: 'What should I do with dumped waste on a lane above town?',
        a: [
          'Report it to Bridgend County Borough Council and leave it in place. The ',
          link('fly-tipping page', 'https://www.bridgend.gov.uk/council/customer-services/report-an-issue/report-fly-tipping/'),
          ' explains public land and private land separately.',
        ],
      },
    ],
    nearby: ['port-talbot', 'bridgend'],
  },
  {
    slug: 'llanelli',
    name: 'Llanelli',
    path: '/areas/llanelli',
    adminArea: 'Carmarthenshire',
    title: 'Waste removal in Llanelli | The WasteGame',
    description: 'Post a Llanelli waste removal job for free. Registered waste carriers send prices. Pay by card only after the job is marked complete. No customer fee.',
    h1: 'Waste removal in Llanelli',
    eyebrow: 'Llanelli, Carmarthenshire',
    card: 'Town-centre terraces, seaside flats, and household waste that is too much for a car to Trostre.',
    intro: [
      'Llanelli is in Carmarthenshire, west of Swansea. Waste removal here might be a terrace near the town centre, a flat by the seafront, a house in Furnace, Dafen, Bynea or Pwll, or a garden in Stradey. Nearby Llangennech and Burry Port are the same county, with the same council tips, but this page is about posting a job for Llanelli itself.',
      'The WasteGame is new and free to post on. Registered waste carriers send a price only if they want the job. You can ignore every price. If you accept one, you pay that amount by card after the job is marked complete. Customers are not charged a fee on top.',
    ],
    howLead: 'Tell the carrier whether the waste is in the town, out at Dafen or Bynea, or in a flat with stairs and no lift. Llanelli postcodes cover a wide patch. The village name matters more than the county.',
    jobs: [
      {
        icon: '🛋️',
        title: 'Furniture',
        text: 'Town terraces and seaside flats often have a sofa or a bed that will not go in a hatchback and will not wait for a bulky round. Say how many large items, and whether they are already outside or still upstairs.',
      },
      {
        icon: '🌿',
        title: 'Garden waste',
        text: 'Hedges, soil and shed clear-outs from Stradey, Pwll and the edges of town. A dozen bags by the gate is easy to describe. A garden that needs cutting as well as taking away should say so, because that is more work.',
      },
      {
        icon: '🛏️',
        title: 'Mattresses',
        text: 'Trostre recycling centre does take mattresses, if you can get them there and you are a Carmarthenshire resident. If you cannot lift one into a car, describe the size and how many you have so a carrier can price a collection.',
      },
      {
        icon: '🧱',
        title: 'Rubble',
        text: 'Bags of brick, tile or soil from your own house, not from a building firm. Say the number of bags. A carrier who turns up to “a bit of rubble” and finds a driveway’s worth will not have priced the job you meant.',
      },
      {
        icon: '🏠',
        title: 'House and end-of-tenancy clearance',
        text: 'A rented house being handed back in Llanelli, or a family home being sold. Separate the rubbish from anything the next occupant is keeping. A list of rooms is enough of a start if you add the bulky items.',
      },
    ],
    localTitle: 'Trostre and fly-tipping in Carmarthenshire',
    local: [
      [
        'The household waste recycling centre for Llanelli is Trostre. Carmarthenshire also has centres at Nantycaws near Carmarthen, Wernddu near Ammanford, and Whitland. Trostre is the local one. The council asks for proof you live in Carmarthenshire, and some vans need a permit. Read ',
        link('the Trostre page', 'https://www.carmarthenshire.gov.wales/council-services/recycling-bins-and-litter/recycling-centres/trostre-llanelli/'),
        ' before you go.',
      ],
      [
        'To report fly-tipping, Carmarthenshire County Council explains the offence on its ',
        link('fly-tipping page', 'https://www.carmarthenshire.gov.wales/council-services/recycling-bins-and-litter/litter/fly-tipping/'),
        ' and links to an online report: ',
        link('report fly-tipping', 'https://myaccount.carmarthenshire.gov.wales/en/service/Report_fly_tipping_process'),
        '. Do not move the waste. ',
        ...registerCheck('Being on the register for where they are based is what “registered waste carrier” means. It is not a promise of a collection.'),
      ],
    ],
    faqs: [
      {
        q: 'Who runs the tip in Llanelli?',
        a: [
          'Carmarthenshire County Council. The Llanelli site is Trostre. Start at the ',
          link('Trostre recycling centre page', 'https://www.carmarthenshire.gov.wales/council-services/recycling-bins-and-litter/recycling-centres/trostre-llanelli/'),
          ' for what to bring and who can enter. It is for household waste from county residents, taken there by you.',
        ],
      },
      {
        q: 'Does a Llanelli job cost anything to post?',
        a: 'No. You pay a carrier only if you accept their price, by card, after the job is marked complete. The WasteGame does not charge customers a fee on top of that price.',
      },
      {
        q: 'I live in Burry Port. Is this the right page?',
        a: 'Burry Port is next door and uses Carmarthenshire’s centres, including the same council rules. You can post a job with the correct town in the description. This page is written for Llanelli. There is a separate page for Swansea, which is a different council.',
      },
      {
        q: 'What does a registration mean for a Llanelli job?',
        a: [
          'It means the carrier is asked to be a registered waste carrier. ',
          ...registerCheck('The WasteGame does not publish a registration number on this page, and a registration is not a promise that someone will accept your job.'),
        ],
      },
      {
        q: 'What if I only have one item?',
        a: 'Post it anyway, and say it is one item. A single mattress or a single sofa is a normal small job. A carrier may still decide not to price it. Posting that one item is free.',
      },
    ],
    nearby: ['swansea', 'carmarthen', 'ammanford'],
  },
  {
    slug: 'bridgend',
    name: 'Bridgend',
    path: '/areas/bridgend',
    adminArea: 'Bridgend County Borough',
    title: 'Rubbish removal in Bridgend | The WasteGame',
    description: 'Post a Bridgend rubbish removal job for free. Registered waste carriers send prices for houses, gardens and clearances. Pay by card only when the job is done.',
    h1: 'Rubbish removal in Bridgend',
    eyebrow: 'Bridgend, Bridgend County Borough',
    card: 'Newer estates in Brackla and Broadlands, older streets in town, and the county’s recycling centres at Pyle and Brynmenyn.',
    intro: [
      'Bridgend town mixes newer estates in Brackla, Broadlands and Coity with older streets closer to the centre and areas such as Wildmill. A new lawn full of turf offcuts is a different rubbish job from a sofa in a terrace. Pencoed and the villages around the town are still Bridgend County Borough. Maesteg, up the Llynfi valley, has its own page because the roads and the local recycling centre are different.',
      'Post a Bridgend job on The WasteGame for free. Registered waste carriers choose whether to send a price. The marketplace is new, so treat a post as a question, not a confirmed collection. If you accept a price, you pay it by card only after the job is marked complete. No customer fee.',
    ],
    howLead: 'Name the area. Brackla, Broadlands, Coity and the town centre are not interchangeable on a map, and parking on a new estate is not the same as parking on an older street. Say if the waste is packaging from a move, garden waste, or a clearance.',
    jobs: [
      {
        icon: '🌿',
        title: 'Garden waste',
        text: 'New estates often need soil, turf and hedge cuttings taken away once the garden is actually planted. Older gardens closer to town may be a shed, a fence and years of branches. Say which, and whether it is bagged.',
      },
      {
        icon: '🛋️',
        title: 'Furniture',
        text: 'Moving into or out of Bridgend leaves sofas and beds that the removal van was not booked to take. List them. A single armchair is not a house clearance, and calling it one makes the price meaningless.',
      },
      {
        icon: '🛏️',
        title: 'Mattresses',
        text: 'Say the size and whether the mattress is dry. If you are a Bridgend County Borough resident and you can transport it, check the community recycling centres first. If you cannot lift it, that is what a collection is for.',
      },
      {
        icon: '🧱',
        title: 'Rubble',
        text: 'Small DIY from a house: tiles, a patio, a kitchen rip-out, in bags. Give a bag count. Trade waste from a site is not what this page describes.',
      },
      {
        icon: '🏠',
        title: 'House and end-of-tenancy clearance',
        text: 'A rented home being handed back, or a house being sold, including on the estates north and east of town. Say how many bedrooms you are clearing and what the bulky pieces are. Leave out anything the landlord is keeping.',
      },
    ],
    localTitle: 'Bridgend recycling centres and fly-tipping',
    local: [
      [
        'Bridgend County Borough has community recycling centres at Pyle, Brynmenyn and Maesteg. For the town, Pyle and Brynmenyn are the ones people usually mean. Maesteg has the third, up the valley. There is a reuse shop, Furniture Revival, at the Pyle centre for items in good enough condition to sell on. Centres are each closed one day a week. Use ',
        link('the recycling centres page', 'https://www.bridgend.gov.uk/residents/recycling-and-waste/recycling-centres/'),
        ' for days and rules, and the ',
        link('reuse shops page', 'https://www.bridgend.gov.uk/residents/recycling-and-waste/recycling-centres/reuse-shops-partnership/'),
        ' for Furniture Revival.',
      ],
      [
        'Report fly-tipping on ',
        link('Bridgend County Borough Council’s fly-tipping page', 'https://www.bridgend.gov.uk/council/customer-services/report-an-issue/report-fly-tipping/'),
        '. For council-owned or public land the council asks you to email cleanupthecounty@bridgend.gov.uk with the place, the time and what was dumped. Private land is reported a different way, explained on that same page. Do not approach anyone dumping waste. ',
        ...registerCheck(),
      ],
    ],
    faqs: [
      {
        q: 'Is posting a Bridgend rubbish job free?',
        a: 'Yes. You pay nothing to post, nothing to read a price, and nothing if you decide not to accept one. Card payment is only for a price you accepted, after the job is marked complete, with no customer fee added.',
      },
      {
        q: 'Which recycling centre should Bridgend residents use?',
        a: [
          'The county lists Pyle, Brynmenyn and Maesteg. Check which is open on the day you go: ',
          link('community recycling centres', 'https://www.bridgend.gov.uk/residents/recycling-and-waste/recycling-centres/'),
          '. Do not leave waste outside a closed gate. The council warns that can be treated as fly-tipping.',
        ],
      },
      {
        q: 'Do you cover Pencoed and the villages?',
        a: 'You can post a job with the village named in the description. A carrier then decides if the distance is one they will price. This page does not promise cover for every village in the county.',
      },
      {
        q: 'How do I report fly-tipping in Bridgend?',
        a: [
          'Go to the ',
          link('council fly-tipping page', 'https://www.bridgend.gov.uk/council/customer-services/report-an-issue/report-fly-tipping/'),
          '. Public-land reports on that page go to cleanupthecounty@bridgend.gov.uk. Include where it is and what you saw. Leave the waste alone.',
        ],
      },
      {
        q: 'What does a carrier registration mean here?',
        a: [
          'It means the person is a registered waste carrier. ',
          ...registerCheck('It does not mean The WasteGame has ranked them, counted them, or promised they will take a Bridgend job.'),
        ],
      },
    ],
    nearby: ['maesteg', 'cardiff', 'port-talbot'],
  },
  {
    slug: 'cardiff',
    name: 'Cardiff',
    path: '/areas/cardiff',
    adminArea: 'City and County of Cardiff',
    title: 'Rubbish removal in Cardiff | The WasteGame',
    description: 'Post a Cardiff rubbish removal or house clearance for free. Registered waste carriers send prices. Pay by card only after the job is marked complete. No customer fee.',
    h1: 'Rubbish removal in Cardiff',
    eyebrow: 'Cardiff, South Wales',
    card: 'End-of-tenancy flats in Cathays and Roath, lane furniture in Canton and Grangetown, and garden waste in Whitchurch and Rhiwbina.',
    intro: [
      'Cardiff rubbish jobs split by neighbourhood. June in Cathays, Roath and Plasnewydd is mattresses and the end of a tenancy. Canton, Grangetown, Splott and Adamsdown are often a sofa in a terrace or a rear lane. Bay flats add stairs and lifts. Whitchurch, Rhiwbina, Heath and Llandaff are more often garden waste and a family house. Write the neighbourhood. “Cardiff” on its own is too vague to price.',
      'The WasteGame is new. Posting is free and it is only a request for prices from registered waste carriers. Nobody is booked until you accept a price, and you might not receive one. If you accept a priced job, you pay that price by card after it is marked complete. The WasteGame does not add a customer fee.',
    ],
    howLead: 'Cardiff access is the whole price: a third-floor flat in Cathays with no lift, a Bay apartment with a service lift, a Grangetown lane, or a driveway in Rhiwbina. Say which, and say if there is anywhere legal to stop.',
    jobs: [
      {
        icon: '🏠',
        title: 'House and end-of-tenancy clearance',
        text: 'Student lets in Cathays and Roath at the end of the academic year, and family homes elsewhere when a tenancy or a sale ends. List bedrooms and the bulky items. Bagged clothes are not the same job as beds and desks. Say if the landlord wants some items left.',
      },
      {
        icon: '🛋️',
        title: 'Furniture',
        text: 'Rear-lane sofas in Canton, Grangetown and Adamsdown, and furniture that will not fit a Bay or city-centre lift. If it is already outside, say so. If it is still in a bedroom three floors up, say that instead.',
      },
      {
        icon: '🛏️',
        title: 'Mattresses',
        text: 'The usual leftover of a Cardiff house share. Count them and give the sizes. Cardiff’s recycling centres do take mattresses if you can get them there and you have booked a slot. A collection is for when you cannot.',
      },
      {
        icon: '🌿',
        title: 'Garden waste',
        text: 'Whitchurch, Rhiwbina, Heath, Llandaff and the city’s older suburbs produce hedges, soil and shed clear-outs that will not go in a food caddy. Say whether the pile is bagged and whether there is side access.',
      },
      {
        icon: '🧱',
        title: 'Rubble',
        text: 'Cardiff Council says its recycling centres are for small DIY, not a large project, and that rubble is capped per visit. A few bags from your own kitchen or path can be described here. A builder’s skip’s worth of mixed waste should not be posted as a household bag job.',
      },
    ],
    localTitle: 'Cardiff tips and fly-tipping',
    local: [
      [
        'Cardiff Council’s household recycling centres are Lamby Way and Bessemer Close. You need to book a slot and bring proof of a Cardiff address. The council asks you not to bring unsorted black bags, and it treats the sites as places for items you cannot put out at the kerbside. Read ',
        link('Cardiff’s recycling centres page', 'https://www.cardiff.gov.uk/ENG/resident/Rubbish-and-recycling/recycling-centres/Pages/default.aspx'),
        ' before you visit.',
      ],
      [
        'Fly-tipping in Cardiff is an offence under section 33 of the Environmental Protection Act 1990. ',
        link('Cardiff Council’s fly-tipping page', 'https://www.cardiff.gov.uk/fly-tipping'),
        ' sets out the offence, including dumped furniture and building waste, and what happens on private land. To report an incident, the council’s ',
        link('Cardiff Gov app page', 'https://www.cardiff.gov.uk/ENG/resident/cardiff-gov-app/Pages/default.aspx'),
        ' lists fly-tipping under Report a problem, and says those reports are also available on the council website. The ',
        link('recycling and waste page', 'https://www.cardiff.gov.uk/ENG/resident/Rubbish-and-recycling/Pages/default.aspx'),
        ' points residents to reporting fly-tipping as well. Do not move the waste. You can check a carrier. ',
        ...registerCheck(),
      ],
    ],
    faqs: [
      {
        q: 'Is a Cardiff rubbish job free to post?',
        a: 'Yes. Posting costs nothing. You pay the price you accept, by card, only after the job is marked complete. There is no customer fee on top.',
      },
      {
        q: 'Will a carrier collect from Cathays or the Bay?',
        a: 'Only if one chooses to send a price and you accept it. The WasteGame is new and does not promise a collection in Cathays, the Bay, or anywhere else in the city. If no price arrives, you have not paid anything.',
      },
      {
        q: 'Which Cardiff tip can I use?',
        a: [
          'Lamby Way and Bessemer Close, if you live in Cardiff, have booked, and the site takes what you are bringing. Details are on ',
          link('the recycling centres page', 'https://www.cardiff.gov.uk/ENG/resident/Rubbish-and-recycling/recycling-centres/Pages/default.aspx'),
          '. Do not turn up without a booking.',
        ],
      },
      {
        q: 'How do I report fly-tipping in Cardiff?',
        a: [
          'Read ',
          link('the council’s fly-tipping page', 'https://www.cardiff.gov.uk/fly-tipping'),
          ', then report it the way the council asks. The Cardiff Gov app lists fly-tipping as something you can report, with the location and a photo. Do not confront anyone you see dumping waste.',
        ],
      },
      {
        q: 'Do I pay cash on the day?',
        a: 'No. On a priced job the payment is by card, and only after the job is marked complete. You pay the price you already accepted. The WasteGame does not add a fee for the customer.',
      },
    ],
    nearby: ['bridgend'],
  },
].concat(NEW_AREAS);

export function areaBySlug(slug) {
  return AREAS.find((area) => area.slug === slug) || null;
}

export function partsToText(value) {
  const parts = Array.isArray(value) ? value : [value];
  return parts.map((part) => {
    if (typeof part === 'string') return part;
    if (part && part.href) return `${part.t} (${part.href})`;
    return part?.t || '';
  }).join('');
}

export const AREAS_INDEX = {
  path: '/areas',
  title: 'Rubbish removal areas in South Wales | The WasteGame',
  description: 'Town pages for rubbish removal across Swansea and South Wales, from Carmarthen to Cardiff. Post a job free. Registered waste carriers send prices. No customer fee.',
  h1: 'Rubbish removal areas in South Wales',
};
